import { useMemo, useState } from 'react';
import { AsyncBoundary, Drawer, Icon, useToast } from '@kollez/design-system';
import { useFacultyMentees, useLogCheckIn, useMenteeCheckIns, type FacultyMentee } from '@/lib/queries';
import { formatIndianDate } from '@kollez/shared';

/**
 * /faculty/mentees (F86c) — direct port of kollez/pages/faculty/mentees.html.
 *   Segmented Needs-you / Applying / Placed · .mt rows with avatar + 6-week .pulse bars
 *   · drawer with recent check-ins, log-new-check-in form, Nexie next-step suggestion
 */
const FMT: Array<[string, 'person' | 'call' | 'whatsapp' | 'video' | 'email']> = [
  ['Met in person', 'person'],
  ['Phone call', 'call'],
  ['WhatsApp', 'whatsapp'],
];

function initials(name: string): string {
  return name.trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

export function FacultyMenteesPage() {
  const { data, isLoading, error, refetch } = useFacultyMentees();
  const [seg, setSeg] = useState<'attn' | 'going' | 'placed'>('attn');
  const [openId, setOpenId] = useState<string | null>(null);

  const mentees = data?.mentees ?? [];
  const counts = useMemo(() => ({
    attn: mentees.filter((m) => m.seg === 'attn').length,
    going: mentees.filter((m) => m.seg === 'going').length,
    placed: mentees.filter((m) => m.seg === 'placed').length,
  }), [mentees]);
  const shown = mentees.filter((m) => m.seg === seg);
  const opened = openId ? mentees.find((m) => m._id === openId) ?? null : null;

  // Fake pulse — server doesn't ship 6-week activity yet; deterministic per-mentee hash so it doesn't jump.
  function pulse(m: FacultyMentee): boolean[] {
    const h = m._id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return [0, 1, 2, 3, 4, 5].map((i) => ((h >> i) & 1) === 1);
  }

  return (
    <div className="stack">
      <style>{`
        .mt { display: grid; grid-template-columns: 36px minmax(0,1fr) auto; gap: 12px; align-items: center; padding: 14px 16px; border-bottom: 1px solid rgb(var(--line-tertiary)); cursor: pointer; color: inherit; text-decoration: none; }
        .mt:last-child { border-bottom: 0; }
        .mt:hover { background: rgb(var(--bg-secondary)); }
        .pulse { display: flex; gap: 3px; }
        .pulse i { width: 6px; height: 16px; border-radius: 2px; background: rgb(var(--bg-tertiary)); }
        .pulse i.on { background: rgb(var(--ink-accent)); }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">B.Tech · Mentees</p>
          <h1 className="h-page">My mentees</h1>
          <p className="subhead">
            {mentees.length} students. {counts.placed} placed. {counts.attn} need you first — start with them.
          </p>
        </div>
      </div>

      <div className="segmented w-full sm:w-auto overflow-x-auto">
        <button aria-pressed={seg === 'attn'} onClick={() => setSeg('attn')}>
          Needs you <span className="helper ml-1">{counts.attn}</span>
        </button>
        <button aria-pressed={seg === 'going'} onClick={() => setSeg('going')}>
          Applying <span className="helper ml-1">{counts.going}</span>
        </button>
        <button aria-pressed={seg === 'placed'} onClick={() => setSeg('placed')}>
          Placed <span className="helper ml-1">{counts.placed}</span>
        </button>
      </div>

      <AsyncBoundary
        kind="table"
        loading={isLoading}
        error={error}
        empty={!isLoading && shown.length === 0}
        emptyTitle="No mentees in this bucket"
        emptyHint="Your head of department assigns mentees. You'll get a note when they do."
        onRetry={() => void refetch()}
      >
        <div className="card">
          {shown.map((m) => (
            <div
              key={m._id}
              className="mt"
              role="button"
              tabIndex={0}
              onClick={() => setOpenId(m._id)}
              onKeyDown={(e) => { if (e.key === 'Enter') setOpenId(m._id); }}
            >
              <span className="avatar">{initials(m.name)}</span>
              <div className="min-w-0">
                <div className="cell-title truncate">{m.name}</div>
                <div className="cell-sub truncate">{m.note ?? m.roll ?? '—'}</div>
              </div>
              <div className="pulse" role="img" aria-label="Activity, last 6 weeks">
                {pulse(m).map((v, i) => <i key={i} className={v ? 'on' : ''} />)}
              </div>
            </div>
          ))}
        </div>
      </AsyncBoundary>

      <p className="helper">The bars show weekly activity over the last 6 weeks: sign-ins, applications and practice.</p>

      {opened && <MenteeDrawer mentee={opened} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function MenteeDrawer({ mentee, onClose }: { mentee: FacultyMentee; onClose: () => void }) {
  const checkins = useMenteeCheckIns(mentee._id);
  const log = useLogCheckIn();
  const toast = useToast();
  const [format, setFormat] = useState<'person' | 'call' | 'whatsapp' | 'video' | 'email'>('person');
  const [text, setText] = useState('');
  const [flag, setFlag] = useState(false);

  async function save() {
    if (text.trim().length < 3) { toast.show({ tone: 'warning', message: 'Say what you talked about.' }); return; }
    try {
      await log.mutateAsync({ menteeUserId: mentee._id, format, text, flaggedToTpo: flag });
      toast.show({ message: 'Check-in saved' + (flag ? ' and flagged to the placement officer.' : '.') });
      onClose();
    } catch { toast.show({ tone: 'warning', message: 'Could not save' }); }
  }

  return (
    <Drawer open onClose={onClose} title={mentee.name} size="lg">
      <p className="eyebrow">{mentee.branch ?? 'Mentee'}</p>
      <h2 className="h-section">{mentee.name}</h2>
      <p className="m-0 mt-2">{mentee.note ?? 'No context yet.'}</p>

      {(checkins.data?.checkins ?? []).slice(0, 3).map((c) => (
        <div key={c._id} className="well p-3 text-sm mt-3">
          <div className="cell-sub">
            {c.format} · {formatIndianDate(c.at)}{c.facultyUserId?.fullName ? ` · ${c.facultyUserId.fullName}` : ''}
          </div>
          <p className="m-0 mt-1">{c.text}</p>
          {c.flaggedToTpo && <span className="badge badge-warning mt-2">Flagged to TPO</span>}
        </div>
      ))}

      <div className="grid gap-2 mt-6">
        <span className="label">Log a check-in</span>
        <div className="flex flex-wrap gap-2">
          {FMT.map(([label, val]) => (
            <button key={val} type="button" className="tag" aria-pressed={format === val} onClick={() => setFormat(val)}>{label}</button>
          ))}
        </div>
        <textarea
          className="textarea"
          rows={3}
          placeholder="What did you talk about? What's the next step?"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <label className="flex gap-2 items-center text-sm">
          <input type="checkbox" className="check" checked={flag} onChange={(e) => setFlag(e.target.checked)} />
          Flag to the placement officer
        </label>
      </div>

      <div className="ai-suggest mt-4">
        <Icon name="sparkles" size={14} />
        <div>
          {mentee.seg === 'attn'
            ? 'Suggested next step: book a mock aptitude test this week and apply to at least one open drive.'
            : mentee.seg === 'going'
            ? 'On track. A short note of encouragement before the next round helps.'
            : 'Placed. Check in monthly until joining.'}
        </div>
      </div>

      <div className="drawer-foot">
        {mentee.phone ? (
          <a
            className="btn btn-outline"
            href={`https://wa.me/${mentee.phone.replace(/\D/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon name="message-circle" size={14} />WhatsApp
          </a>
        ) : (
          <button className="btn btn-outline" type="button" disabled title="No phone on record"><Icon name="message-circle" size={14} />WhatsApp</button>
        )}
        <button className="btn btn-primary ml-auto" onClick={save} disabled={log.isPending}>
          {log.isPending ? 'Saving…' : 'Save check-in'}
        </button>
      </div>
    </Drawer>
  );
}
